import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, UserRound } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "./Logo";
import { useCart } from "../context/CartContext";
import { useCustomer } from "../context/CustomerContext";
import { scrollToId } from "../lib/api";

const LINKS = [
  ["catalogo", "Productos"],
  ["kits", "Kits"],
  ["asistente", "Asistente IA"],
  ["dosificacion", "Tabla de cultivo"],
  ["mi-cultivo", "Mi cultivo"],
  ["riego", "Riego"],
  ["calendario-lunar", "Calendario lunar"],
  ["tecnologia", "Tecnología"],
];

export const Nav = () => {
  const { count, setOpen } = useCart();
  const { user } = useCustomer();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const { pathname } = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const go = (id) => {
    setMenu(false);
    if (pathname !== "/") {
      nav("/");
      setTimeout(() => scrollToId(id), 400);
    } else scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled ? "bg-[#0E2A1C]/80 backdrop-blur-xl border-[#8CD05A]/15" : "bg-gradient-to-b from-[#0E2A1C]/95 via-[#0E2A1C]/60 to-transparent border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link"><Logo /></Link>
        <nav className="hidden xl:flex items-center gap-5">
          {LINKS.map(([id, label]) => (
            <button key={id} data-testid={`nav-link-${id}`} onClick={() => go(id)}
              className="group relative font-mono-d text-[11px] tracking-[0.14em] uppercase text-[#C9D6C2] hover:text-[#F6EEDC] transition-colors">
              {label}
              <span className="absolute -bottom-1.5 left-0 h-px w-full origin-left scale-x-0 bg-[#F2B441] transition-transform duration-500 group-hover:scale-x-100" />
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/cuenta" data-testid="nav-account-button" aria-label={user ? "Mi cuenta" : "Iniciar sesión"}
            className="flex items-center gap-2 rounded-full border border-[#8CD05A]/30 px-3 sm:px-4 h-10 text-sm hover:border-[#F2B441] hover:text-[#F2B441] transition-colors">
            <UserRound className="w-4 h-4" />
            <span className="hidden sm:inline font-mono-d text-[11px] tracking-[0.2em] uppercase max-w-[120px] truncate">{user ? user.name.split(" ")[0] : "Entrar"}</span>
          </Link>
          <button data-testid="cart-drawer-trigger" onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 rounded-full border border-[#8CD05A]/30 px-4 h-10 text-sm hover:border-[#F2B441] hover:text-[#F2B441] transition-colors">
            <ShoppingBag className="w-4 h-4" />
            <span className="hidden sm:inline font-mono-d text-[11px] tracking-[0.2em]">CARRITO</span>
            <motion.span key={count} initial={{ scale: 0.4 }} animate={{ scale: 1 }} data-testid="cart-count-badge"
              className="min-w-[22px] h-[22px] grid place-items-center rounded-full bg-[#8CD05A] text-[#0E2A1C] text-[11px] font-bold">
              {count}
            </motion.span>
          </button>
          <button data-testid="mobile-menu-toggle" onClick={() => setMenu((m) => !m)} className="xl:hidden w-10 h-10 grid place-items-center rounded-full border border-[#8CD05A]/30">
            {menu ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {menu && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="xl:hidden overflow-hidden bg-[#0E2A1C]/95 backdrop-blur-xl border-b border-[#8CD05A]/15">
            <div className="px-5 py-6 flex flex-col gap-5">
              {LINKS.map(([id, label]) => (
                <button key={id} data-testid={`mobile-nav-${id}`} onClick={() => go(id)} className="text-left font-serif-d text-3xl">{label}</button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
